import { Module, Mutation, Action } from 'vuex-module-decorators'
import dayjs from 'dayjs'
import {
  GetReferenceRequest,
  Reference,
  referenceApi,
  ReferenceImage,
  ReferenceBibliography,
  SystemCode,
  defaultSystemCode
} from '@/api'
import BaseStore from '@/store/BaseStore'

// TODO default値にnullを指定しないと更新処理が出来なくなるので取り敢えずこの状態で提出
const defaultItem = (): Reference => ({
  id: 0,
  question: '',
  level: defaultSystemCode(),
  managementNo: '',
  answer: '',
  createdDate: dayjs().format('YYYYMMDD'),
  answerStage: defaultSystemCode(),
  keyword: '',
  ndc1: '',
  ndc1Edition: '',
  ndc2: '',
  ndc2Edition: '',
  ndc3: '',
  ndc3Edition: '',
  researchType: '',
  contentsType: '',
  referenceMaterials: '',
  answerProcess: '',
  referee: '',
  preResearch: '',
  free: '',
  contributor: '',
  questionerUserId: 0,
  questionerName: '',
  questionerKana: '',
  questionerType: '',
  questionerPhone: '',
  questionerFax: '',
  questionerMail: '',
  questionerAddress: '',
  receptionWindow: '',
  receptionDate: '',
  receptionMethod: '',
  respondent: '',
  responseDate: '',
  responseMethod: '',
  modelNumber: '',
  memo: '',
  database: defaultSystemCode(),
  opacShow: false,
  createdId: 0,
  updatedId: 0,
  createdAt: '',
  updatedAt: '',
  bibliographies: [],
  images: []
})

@Module({
  name: 'ReferenceStore',
  namespaced: true,
  stateFactory: true
})
export default class ReferenceStore extends BaseStore {
  item: Reference = defaultItem()

  @Mutation
  public reset() {
    this.item = defaultItem()
  }

  get getItem(): Reference {
    return this.item
  }

  @Mutation
  public setItem(item: Reference) {
    this.item = item
  }

  @Mutation
  public setManagementNo(value: string) {
    this.item.managementNo = value
  }

  @Mutation
  public setAnswerStage(answerStage: SystemCode) {
    this.item.answerStage = answerStage
  }

  @Mutation
  public setLevel(level: SystemCode) {
    this.item.level = level
  }

  @Mutation
  public setDatabase(database: SystemCode) {
    this.item.database = database
  }

  @Mutation
  public setOpacShow(opacShow: boolean) {
    this.item.opacShow = opacShow
  }

  @Mutation
  public setQuestion(value: string) {
    this.item.question = value
  }

  @Mutation
  public setAnswer(value: string) {
    this.item.answer = value
  }

  @Mutation
  public setKeyword(value: string) {
    this.item.keyword = value
  }

  @Mutation
  public setNdc1(value: string) {
    this.item.ndc1 = value
  }

  @Mutation
  public setNdc1Edition(value: string) {
    this.item.ndc1Edition = value
  }

  @Mutation
  public setCreatedDate(value: string) {
    this.item.createdDate = value
  }

  @Mutation
  public setResearchType(value: string) {
    this.item.researchType = value
  }

  @Mutation
  public setContentsType(value: string) {
    this.item.contentsType = value
  }

  @Mutation
  public setQuestionerType(value: string) {
    this.item.questionerType = value
  }

  @Mutation
  public setReferenceMaterials(value: string) {
    this.item.referenceMaterials = value
  }

  @Mutation
  public setAnswerProcess(value: string) {
    this.item.answerProcess = value
  }

  @Mutation
  public setReferee(value: string) {
    this.item.referee = value
  }

  @Mutation
  public setPreResearch(value: string) {
    this.item.preResearch = value
  }

  @Mutation
  public setFree(value: string) {
    this.item.free = value
  }

  @Mutation
  public setContributor(value: string) {
    this.item.contributor = value
  }

  @Mutation
  public setQuestionerName(value: string) {
    this.item.questionerName = value
  }

  @Mutation
  public setQuestionerKana(value: string) {
    this.item.questionerKana = value
  }

  @Mutation
  public setQuestionerAddress(value: string) {
    this.item.questionerAddress = value
  }

  @Mutation
  public setQuestionerPhone(value: string) {
    this.item.questionerPhone = value
  }

  @Mutation
  public setQuestionerFax(value: string) {
    this.item.questionerFax = value
  }

  @Mutation
  public setQuestionerMail(value: string) {
    this.item.questionerMail = value
  }

  @Mutation
  public setReceptionDate(value: string) {
    this.item.receptionDate = value
  }

  @Mutation
  public setReceptionMethod(value: string) {
    this.item.receptionMethod = value
  }

  @Mutation
  public setReceptionWindow(value: string) {
    this.item.receptionWindow = value
  }

  @Mutation
  public setResponseDate(value: string) {
    this.item.responseDate = value
  }

  @Mutation
  public setResponseMethod(value: string) {
    this.item.responseMethod = value
  }

  @Mutation
  public setRespondent(value: string) {
    this.item.respondent = value
  }

  @Mutation
  public setMemo(value: string) {
    this.item.memo = value
  }

  @Mutation
  public setModelNumber(value: string) {
    this.item.modelNumber = value
  }

  @Mutation
  public setNdc2(value: string) {
    this.item.ndc2 = value
  }

  @Mutation
  public setNdc2Edition(value: string) {
    this.item.ndc2Edition = value
  }

  @Mutation
  public setNdc3(value: string) {
    this.item.ndc3 = value
  }

  @Mutation
  public setNdc3Edition(value: string) {
    this.item.ndc3Edition = value
  }

  @Mutation
  public setBibliographies(value: ReferenceBibliography[]) {
    this.item.bibliographies = value
  }

  @Mutation
  public setImages(value: ReferenceImage[]) {
    this.item.images = value
  }

  @Action
  public async fetchById(id: number) {
    const request: GetReferenceRequest = {
      id
    }

    try {
      const item: Reference = await referenceApi.getReferenceById(request)
      this.setItem(item)
    } catch (e) {
      if (e.status === 404) {
        alert('データが見つかりませんでした。')
      } else {
      }
    }
  }

  @Action({ rawError: true })
  public async create() {
    const item: Reference = await referenceApi.create({
      reference: this.item
    })
    this.setItem(item)
  }

  @Action({ rawError: true })
  public async update() {
    const item: Reference = await referenceApi.update({
      reference: this.item
    })
    this.setItem(item)
  }
}
